import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FormRenderer } from '@kevlarcon/formkit';
import '../../styles/formkitTheme.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../components/AdminLayout';
import { formKitClient } from '../../api/formkitClient';
import { stateContentApi } from '../../api/stateContent';

/**
 * One state's content, edited through formkit's FormRenderer against that
 * state's row — the tenant-designed fields plus any tables (manifests), whose
 * widgets load and save the state's rows themselves. Saving goes live
 * immediately (docs/plans/state-content.md: no draft step in v1).
 */
const StateContentEditor: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const queryClient = useQueryClient();
  const [saved, setSaved] = React.useState(false);
  // FormRenderer ends on its applicant "thank you" screen after a submit;
  // remounting it drops that and reloads the saved row, so an admin keeps
  // editing in place.
  const [saves, setSaves] = React.useState(0);

  const { data: form } = useQuery({
    queryKey: ['state-content-form'],
    queryFn: stateContentApi.getForm,
  });
  const { data: row, isError } = useQuery({
    queryKey: ['state-content-row', form?.id, code.toUpperCase()],
    queryFn: () => stateContentApi.getStateRow(form!.id, code),
    enabled: !!form,
  });

  const title = `${row?.full_name ?? code.toUpperCase()} content`;
  const actions = (
    <Link to="/settings/states" className="sgk-btn sgk-btn--onnavy sgk-btn--sm" data-testid="state-content-back-link">
      All states
    </Link>
  );

  return (
    <AdminLayout title={title} actions={actions} wide>
      {saved && (
        <div className="sgk-alert sgk-alert--success" data-testid="state-content-saved-alert">
          Saved.
        </div>
      )}
      {isError ? (
        <p className="sgk-admin__sub" data-testid="state-content-error">No such state.</p>
      ) : !form || !row ? (
        <p className="sgk-admin__sub" data-testid="state-content-loading">Loading…</p>
      ) : (
        <FormRenderer
          key={`${row.id}-${saves}`}
          client={formKitClient}
          formId={form.id}
          submissionId={row.id}
          cacheKey={`state-content-${row.id}`}
          variant="editor"
          onSubmitted={async () => {
            setSaved(true);
            await queryClient.invalidateQueries({ queryKey: ['formkit-submission'] });
            setSaves((n) => n + 1);
          }}
        />
      )}
    </AdminLayout>
  );
};

export default StateContentEditor;
